import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext';

/**
 * Área do editor de documentos.
 *
 * Mesma regra do bônus da biblioteca de prompts: o administrador libera **pessoa
 * por pessoa**, sem depender de plano nem de pagamento. Quem não tem a liberação
 * não vê o menu e, se digitar o endereço na mão, volta para a área dele — sem
 * tela de "recurso bloqueado", que só entregaria que a área existe.
 *
 * A checagem de verdade é do servidor (`/api/documentos` responde 403 sem a
 * liberação), então revogar o acesso vale na hora, mesmo com a página aberta.
 */
export function RotaDoEditorDeDocumentos({ children }: { children: React.ReactNode }) {
  const { usuario, carregando, recarregar } = useAuth();
  const local = useLocation();

  /*
   * A liberação é decidida no banco: ao entrar na área perguntamos de novo, para
   * quem acabou de ser liberado não precisar recarregar o navegador.
   *
   * A dependência é o **id** do usuário, e não o objeto `usuario`: reler o perfil
   * devolve um objeto novo a cada chamada, e um efeito que dependesse dele
   * chamaria `recarregar` de novo — laço infinito de `/api/auth/eu` (e de tudo o
   * que depende do usuário, como a licença), que em um minuto estoura o limite de
   * requisições da API e **bana o IP de quem está navegando**. O id é o mesmo
   * enquanto for a mesma pessoa, e é isso que o efeito quer dizer.
   */
  const idDoUsuario = usuario?.id ?? '';
  useEffect(() => {
    if (!carregando && idDoUsuario) void recarregar();
  }, [carregando, idDoUsuario, recarregar]);

  if (carregando || !usuario) {
    if (!carregando && !usuario) {
      return <Navigate to="/entrar" replace state={{ de: local.pathname }} />;
    }
    return (
      <div className="flex min-h-[60vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Carregando sua área...</span>
      </div>
    );
  }

  if (!usuario.editorDeDocumentos && !usuario.admin) {
    /*
     * Sem acesso, o caminho é a **área de quem escreve** — e não a lista de miolos.
     * É lá que a pessoa vê o que o editor faz e pega o acesso gratuito num clique;
     * mandá-la para o outro produto era oferecer agenda a quem veio escrever.
     */
    return <Navigate to="/escrever" replace />;
  }

  return <>{children}</>;
}
